import { Filter } from 'app/modules/filters/models';
import { FintracFilingStatus } from 'app/modules/fintracStr/enums';
import { ListFintracStrFilingsRequest } from 'app/modules/fintracStr/requests';
import {
  FintracFormFields,
  FintracStrFilingContent,
  FintracStrFilingContentWithErrors,
  FintracStrFormErrors,
} from 'app/modules/fintracStr/models';
import {
  getFormSectionValues,
  getFormValues,
  getSectionValues,
} from 'app/modules/fintracStr/transformers';
import {
  FINTRAC_STR_FLATTENED_FORM_CONFIG,
  FINTRAC_STR_FORM_FIELDS,
} from 'app/modules/fintracStr/constants';
import { FINTRAC_STR_DATE_FORMAT } from 'app/modules/fintracStr/formConstants';
import { capitalize, isArray, isPlainObject, isUndefined } from 'lodash';
import { flattenValues } from 'app/modules/fintracStr/formUtils';
import { setIn } from 'final-form';
import { parse, isValid } from 'date-fns';

export const parseFintracDate = (date?: string): Date | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = parse(date, FINTRAC_STR_DATE_FORMAT, new Date(0));

  if (isValid(parsed)) {
    return parsed;
  }

  return undefined;
};

export const isFintracFilingSubmitted = (
  fintracFilingStatus: FintracFilingStatus,
): boolean => {
  return [
    FintracFilingStatus.SUBMITTED_WITH_WARNINGS,
    FintracFilingStatus.SUBMITTED_WITH_SUCCESS,
  ].includes(fintracFilingStatus);
};

export const createFintracStrFilterPayload = (
  filters: Filter[],
): Pick<ListFintracStrFilingsRequest, 'filters'> => {
  // laying groundwork for adding more filters
  return {
    filters: filters.reduce((acc: Filter[], i) => {
      acc.push(i);
      return acc;
    }, []),
  };
};

/**
 * Creates initial form values for FINTRAC STR forms by merging default form configuration
 * with provided initial values while filtering out fields not defined in the configuration.
 *
 * This function acts as a selective merge operation that:
 * 1. Uses the FINTRAC_STR_FORM_FIELDS configuration as the authoritative structure
 * 2. Extracts only values from initialValues that correspond to fields defined in the config
 * 3. Maintains the proper nested structure required by the form
 * 4. Handles both regular and repeatable sections appropriately
 * 5. Ignores any extraneous fields not present in the form configuration
 *
 * The function ensures type safety and consistency by:
 * - Flattening the initial values for easier field access
 * - Using getSectionValues to properly extract and structure field values
 * - Respecting repeatable section configurations
 * - Building a properly typed FintracStrFilingContent object
 *  *
 * @param initialValues - Raw initial values that may contain extra fields not in the form config
 * @returns Properly structured form values containing only fields defined in FINTRAC_STR_FORM_FIELDS
 */
export const createFintracStrFormInitialValues = (
  initialValues: Record<string, any>,
): FintracStrFilingContent =>
  FINTRAC_STR_FORM_FIELDS.reduce((acc, tabConfig) => {
    const {
      fields,
      repeatable,
      defaultNumOfEntries,
      name: tabPath,
    } = tabConfig.tabSection;

    acc[tabPath] = getSectionValues(
      tabPath,
      fields,
      initialValues,
      repeatable,
      defaultNumOfEntries,
    );

    return acc;
  }, {});

/**
 * Transforms form values into a structured payload for FINTRAC STR filings.
 *
 * This function recursively processes form values and converts them into a format
 * suitable for FINTRAC STR submissions, following specific existence and value requirements
 * defined in the configuration. The function ensures that fields are included or excluded
 * based on the rules:
 * - Fields must exist in payload if config.mustExistInPayload is true
 * - Fields must have a value if config.mustHaveValueIfExist is true
 *
 * The function handles three types of values:
 * 1. Objects: Recursively processes nested objects
 * 2. Arrays: Processes arrays of objects or primitive values
 * 3. Primitives: Directly maps primitive values
 *
 * @param formValues - The form values to be transformed
 * @param flattenedConfig - Configuration object containing field definitions and transformations
 * @param prefix - Optional prefix for nested field paths (used internally for recursion)
 * @returns Transformed form values in the format required for FINTRAC STR filings
 */
export const recursivelyCreateFintracStrFormPayload = (
  flattenedValues: Record<string, any>,
  formValues: FintracStrFilingContent,
  currValues: Record<string, any>,
  prefix: string = '',
  indexes: number[] = [],
): FintracStrFilingContent => {
  return currValues && isPlainObject(currValues)
    ? Object.entries(currValues).reduce((acc, [key, value]) => {
        const rawPath = prefix ? `${prefix}.${key}` : key;
        const currConfig =
          FINTRAC_STR_FLATTENED_FORM_CONFIG[rawPath] ?? // Get config if section is not repeatable
          FINTRAC_STR_FLATTENED_FORM_CONFIG[`${rawPath}[*]`]; // Get config if section is repeatable

        if (isUndefined(currConfig) || currConfig.length > 1) {
          // Config should always be defined and should only have 1 config for internal fields
          // (Internal fields are fields that are rendered within a form, excluding fields that are needed for submission but not required for validation)
          // (E.g. onlyDateOfPosting, datetimeOfPosting are internal fields while dateOfPosting is an external field since it does not exist as one of the fields)
          return acc;
        }

        const selectedConfig = currConfig[0]; // We always have only 1 config for internal fields

        const newPath = selectedConfig.repeatable ? `${rawPath}[*]` : rawPath; // We are doing this here since we only have context on whether the current path is repeatable after we have the config

        // Case where current value is for a section
        if (
          selectedConfig.type === 'section' ||
          selectedConfig.type === 'tabSection'
        ) {
          // Case where current value is for a repeatable section
          if (selectedConfig.repeatable) {
            const values = (value ?? []).reduce((innerAcc, item, index) => {
              if (item && Object.keys(item).length > 0) {
                const newIndexes = [...indexes, index];

                const currValue = recursivelyCreateFintracStrFormPayload(
                  flattenedValues,
                  formValues,
                  item,
                  newPath,
                  newIndexes,
                );

                innerAcc.push(currValue);
              }
              return innerAcc;
            }, []);

            return getFormSectionValues(
              acc,
              FINTRAC_STR_FLATTENED_FORM_CONFIG[newPath],
              values,
              key,
              flattenedValues,
              indexes,
            );
          }

          // Case where current value is for a non-repeatable section
          return getFormSectionValues(
            acc,
            FINTRAC_STR_FLATTENED_FORM_CONFIG[newPath],
            recursivelyCreateFintracStrFormPayload(
              flattenedValues,
              formValues,
              value,
              newPath,
              indexes,
            ),
            key,
            flattenedValues,
            indexes,
          );
        }

        // Case where current value is for a primitive but repeatable field
        if (selectedConfig.repeatable) {
          return getFormValues(
            acc,
            FINTRAC_STR_FLATTENED_FORM_CONFIG[newPath],
            value,
            key,
            formValues,
            flattenedValues,
            indexes,
            false, // We want to include empty values for repeatable form fields to ensure the order is maintained
          );
        }

        // Case where current value is for a primitive but non-repeatable field
        return getFormValues(
          acc,
          FINTRAC_STR_FLATTENED_FORM_CONFIG[newPath],
          value,
          key,
          formValues,
          flattenedValues,
          indexes,
        );
      }, {})
    : {};
};

export const createFintracStrFormPayload = (
  formValues: FintracStrFilingContent,
): FintracStrFilingContent => {
  return recursivelyCreateFintracStrFormPayload(
    flattenValues(formValues),
    formValues,
    formValues,
  );
};

/**
 * Creates default form values for FINTRAC STR forms by recursively extracting default values
 * from the form field configurations.
 *
 * This function operates similarly to createFintracStrFormInitialValues but instead of merging
 * with provided initial values, it extracts default values directly from the form configuration.
 * It:
 * 1. Uses the FINTRAC_STR_FORM_FIELDS configuration as the authoritative structure
 * 2. Recursively traverses all sections and fields to extract defaultValue properties
 * 3. Maintains the proper nested structure required by the form
 * 4. Handles both regular and repeatable sections appropriately
 * 5. Returns undefined for fields that don't have default values specified
 *
 * The function ensures consistency by:
 * - Respecting repeatable section and field configurations
 * - Building a properly typed FintracStrFilingContent object
 * - Using the same structure as the form expects
 *
 * @returns Properly structured form values containing default values from FINTRAC_STR_FORM_FIELDS
 */
export const createFintracStrFormDefaultValues =
  (): FintracStrFilingContent => {
    const processFields = (fields: FintracFormFields): Record<string, any> => {
      const fieldDefaultValues: Record<string, any> = {};

      fields.forEach((fieldConfig) => {
        // Handle section fields
        if (!isArray(fieldConfig) && fieldConfig.type === 'section') {
          if (fieldConfig.repeatable) {
            // For repeatable field sections, return an array with one default entry
            fieldDefaultValues[fieldConfig.name] = [
              processFields(fieldConfig.fields),
            ];
            return;
          }

          fieldDefaultValues[fieldConfig.name] = processFields(
            fieldConfig.fields,
          );
          return;
        }

        // Handle array fields (fields grouped in rows)
        if (isArray(fieldConfig)) {
          for (const rowConfig of fieldConfig) {
            if (rowConfig.repeatable) {
              const numEntries =
                typeof rowConfig.defaultNumOfEntries === 'function'
                  ? rowConfig.defaultNumOfEntries()
                  : (rowConfig.defaultNumOfEntries ?? 1);

              fieldDefaultValues[rowConfig.name] =
                numEntries === 0
                  ? []
                  : Array(numEntries).fill(rowConfig.defaultValue);
            } else {
              fieldDefaultValues[rowConfig.name] = rowConfig.defaultValue;
            }
          }
          return;
        }

        // Handle regular fields
        if (fieldConfig.repeatable) {
          const numEntries =
            typeof fieldConfig.defaultNumOfEntries === 'function'
              ? fieldConfig.defaultNumOfEntries()
              : (fieldConfig.defaultNumOfEntries ?? 1);

          fieldDefaultValues[fieldConfig.name] =
            numEntries === 0
              ? []
              : Array(numEntries).fill(fieldConfig.defaultValue);
        } else {
          fieldDefaultValues[fieldConfig.name] = fieldConfig.defaultValue;
        }
      });

      return fieldDefaultValues;
    };

    return FINTRAC_STR_FORM_FIELDS.reduce((acc, tabConfig) => {
      const tabPath = tabConfig.tabSection.name;
      const sectionDefaults = processFields(tabConfig.tabSection.fields);

      return {
        ...acc,
        [tabPath]: tabConfig.tabSection.repeatable
          ? [sectionDefaults]
          : sectionDefaults,
      };
    }, {});
  };

export const createFintracStrFormErrors = (
  errors: FintracStrFilingContentWithErrors['errors'],
): FintracStrFormErrors => {
  return (errors || []).reduce((acc, error) => {
    const errorMessage = capitalize(error.message);
    const indexes: number[] = [];
    const genericPath = error.path.replace(/\[(\d+)\]/g, (_, index) => {
      indexes.push(Number(index));
      return '[*]';
    });

    const configs = FINTRAC_STR_FLATTENED_FORM_CONFIG[genericPath];
    if (!configs || !Array.isArray(configs) || configs.length === 0) {
      // If any of the value is undefined, we don't want to set the error
      return acc;
    }

    return configs.reduce((innerAcc, config) => {
      const mapping = config.payloadErrorPathToFormErrorPathConversion
        ? config.payloadErrorPathToFormErrorPathConversion(indexes, false)
        : undefined;

      const targetFormPath = mapping?.formPath ?? error.path;
      return setIn(innerAcc, targetFormPath, errorMessage);
    }, acc);
  }, {});
};

export const createFintracStrFormErrorsPaths = (
  errors: FintracStrFilingContentWithErrors['errors'],
): string[] => {
  const allPaths: string[] = [];

  (errors || []).forEach((error) => {
    const indexes: number[] = [];
    const genericPath = error.path.replace(/\[(\d+)\]/g, (_, index) => {
      indexes.push(Number(index));
      return '[*]';
    });

    const configs = FINTRAC_STR_FLATTENED_FORM_CONFIG[genericPath];
    if (!configs || !Array.isArray(configs) || configs.length === 0) {
      // If any of the value is undefined, we don't want to add the path into the list
      return;
    }

    configs.forEach((config) => {
      const mapping = config.payloadErrorPathToFormErrorPathConversion
        ? config.payloadErrorPathToFormErrorPathConversion(indexes, false)
        : undefined;

      allPaths.push(mapping?.formPath ?? error.path);
    });
  });

  return allPaths;
};
